// src/parametres/utilisateurs/NouvelUtilisateur.jsx
import React, { useState } from "react";
import { Card, Row, Col, Form, Button, Spinner, InputGroup } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn"><i className="ri-close-line fs-5"></i></button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

export default function NouvelUtilisateur() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const returnTo = queryParams.get('returnTo') || '/parametres/utilisateurs';

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });
    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    const [form, setForm] = useState({
        nom_utilisateur: '',
        prenom_utilisateur: '',
        email: '',
        mot_de_passe: '',
        telephone: '',
        role: 'commercial'
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.nom_utilisateur.trim() || !form.prenom_utilisateur.trim()) {
            setError("Nom et prénom sont obligatoires");
            return;
        }
        if (!form.email.trim()) {
            setError("L'email est obligatoire");
            return;
        }
        if (!form.mot_de_passe || form.mot_de_passe.length < 6) {
            setError("Le mot de passe doit contenir au moins 6 caractères");
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/utilisateurs.php?action=create`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const result = await res.json();

            if (result.success) {
                showToast('success', 'Utilisateur créé',
                    `${form.prenom_utilisateur} ${form.nom_utilisateur} a été créé avec succès.`);
                setTimeout(() => {
                    if (window.opener) {
                        window.opener.location.reload();
                        window.close();
                    } else {
                        navigate(returnTo);
                    }
                }, 1500);
            } else {
                setError(result.message || "Erreur lors de la création");
                showToast('error', 'Erreur', result.message || "Impossible de créer l'utilisateur");
            }
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur de connexion', err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                <div className="container-fluid">
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper"><i className="ri-user-add-line"></i></div>
                            <div>
                                <h5 className="header-title">Nouvel utilisateur</h5>
                                <p className="text-muted small mb-0">Créer un compte pour un employé</p>
                            </div>
                        </div>
                        <Button className="btn-modern-outline" onClick={() => navigate(returnTo)}>
                            <i className="ri-arrow-left-line"></i> Retour
                        </Button>
                    </div>

                    {error && <div className="alert alert-danger mb-4">{error}</div>}

                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-user-line me-2"></i> Informations de l'utilisateur
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Nom <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            value={form.nom_utilisateur}
                                                            onChange={(e) => setForm({ ...form, nom_utilisateur: e.target.value })}
                                                            disabled={saving}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Prénom <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            value={form.prenom_utilisateur}
                                                            onChange={(e) => setForm({ ...form, prenom_utilisateur: e.target.value })}
                                                            disabled={saving}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Email <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={form.email}
                                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                            disabled={saving}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="tel"
                                                            value={form.telephone}
                                                            onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                                                            disabled={saving}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Mot de passe <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-lock-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="password"
                                                            value={form.mot_de_passe}
                                                            onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })}
                                                            placeholder="Min. 6 caractères"
                                                            disabled={saving}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Rôle</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-shield-user-line"></i></InputGroup.Text>
                                                        <Form.Select
                                                            value={form.role}
                                                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                                                            disabled={saving}
                                                        >
                                                            <option value="admin">Administrateur</option>
                                                            <option value="gestionnaire">Gestionnaire</option>
                                                            <option value="commercial">Commercial</option>
                                                            <option value="magasinier">Magasinier</option>
                                                            <option value="autre">Autre</option>
                                                        </Form.Select>
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Les menus accessibles dépendront du rôle attribué.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button type="button" className="btn-modern-outline" onClick={() => navigate(returnTo)} disabled={saving}>
                                                        Annuler
                                                    </Button>
                                                    <Button type="submit" className="btn-modern-primary" disabled={saving}>
                                                        {saving ? <><Spinner size="sm" className="me-2" />Création...</> : <><i className="ri-save-line me-2"></i>Créer l'utilisateur</>}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                .header-modern { background: white; padding: 1.2rem 1.8rem; border-left: 4px solid ${COLORS.secondary}; border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary}); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .card-header-gradient { background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark || '#1e3f7a'}); border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; padding: 1rem 1.5rem; }
                .form-label-modern { font-weight: 600; color: #334155; font-size: 0.85rem; }
                .input-modern .input-group-text { background: #f8fafc; border: 2px solid #e2e8f0; border-right: none; color: ${COLORS.primary}; border-radius: 10px 0 0 10px; }
                .input-modern .form-control, .input-modern .form-select { border: 2px solid #e2e8f0; border-left: none; border-radius: 0 10px 10px 0; padding: 0.6rem 0.9rem; }
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; color: #4b5563; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary { background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark || '#1e3f7a'}); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-progress { height: 3px; animation: shrink linear forwards; }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }
            `}</style>
        </>
    );
}